import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';

import { getStatuses, statusesSelector } from 'src/store/exchanges';
import {
  adminOrderErrorsSelector,
  getAdminEditOrder,
  isLoadingOrderSelector,
  selectedAdminOrderSelector,
  setAdminOrderErrors,
  setSelectedAdminOrder,
  updateAdminOrder,
} from 'src/store/adminOrders';
import { getAdminCurrencies } from 'src/store/adminCurrency';

import {
  CustomButton,
  CustomInput,
  CustomPagination,
  CustomSelect,
  PageLoader,
  WhiteCard,
} from 'src/components';

import './applicationForm.scss';
import moment from 'moment';

export const ApplicationForm = (props) => {
  const { t } = useTranslation('applicationForm');

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const params = useParams();

  const isEditMode = pathname?.includes('edit');

  const selectedOrder = useSelector(selectedAdminOrderSelector);
  const isLoading = useSelector(isLoadingOrderSelector);
  const formError = useSelector(adminOrderErrorsSelector);
  const statuses = useSelector(statusesSelector);

  const updatedStatuses =
    statuses?.map((item) => ({ value: item, label: t(`statuses:${item}`) })) || [];

  const [formData, setFormData] = useState({
    created_at: '',
    browser: '',
    country: '',
    ip: '',
    status: '',
    login: '',
    token: '',
    payment_processing_merchant: '',
    incoming_processing_merchant: '',
    time_to_pay: '',
    finish_date: '',
    payment_date: '',
    currencyGiven: {
      code: '',
    },
    currencyReceived: {
      code: '',
    },
    payment_transaction_identifier: '',
    pay_transaction_identifier: '',
    type_max_exchange: '',
    max_exchange: '',
    type_min_exchange: '',
    min_exchange: '',
    rate_given_currency: '',
    rate_received_currency: '',
    currencyReceived_reserve: '',
    fields: [],
    transaction: {},
  });
  const [selectedTab, setSelectedTab] = useState('1');

  useEffect(() => {
    if (isEditMode) {
      dispatch(getAdminEditOrder(params?.id));
    }

    dispatch(getStatuses());
    dispatch(getAdminCurrencies());

    return () => {
      dispatch(setSelectedAdminOrder({}));
      dispatch(setAdminOrderErrors({}));
    };
  }, [dispatch, isEditMode, params?.id]);

  useEffect(() => {
    if (isEditMode) {
      setFormData((prev) => ({ ...prev, ...selectedOrder }));
    }
  }, [isEditMode, selectedOrder]);

  const onSubmit = useCallback(
    async (event) => {
      event.preventDefault();

      if (isEditMode) {
        await dispatch(updateAdminOrder({ id: params?.id, payload: formData, navigate }));
        return;
      }
    },
    [dispatch, navigate, formData, isEditMode, params?.id],
  );

  const onClickTab = useCallback(
    (tab) => () => {
      setSelectedTab(tab);
    },
    [],
  );

  const onChangeInput = useCallback(
    (key) => (event) => {
      setFormData((prev) => ({ ...prev, [key]: event.target.value }));

      const copiedErrors = _.cloneDeep(formError);

      delete copiedErrors[key];

      dispatch(setAdminOrderErrors(copiedErrors));
    },
    [dispatch, formError],
  );

  const onChangeSelect = useCallback(
    (key) => (event) => {
      setFormData((prev) => ({ ...prev, [key]: event.value }));

      const copiedErrors = _.cloneDeep(formError);

      delete copiedErrors[key];

      dispatch(setAdminOrderErrors(copiedErrors));
    },
    [dispatch, formError],
  );

  const onChangeFieldsInput = useCallback(
    (field_name) => (event) => {
      const fields = formData?.fields || '';

      const updatedFields = fields.map((item) =>
        item.field_name === field_name ? { ...item, name: event.target.value } : item,
      );

      setFormData((prev) => ({ ...prev, fields: updatedFields }));

      const copiedErrors = _.cloneDeep(formError);

      delete copiedErrors[`fields.${field_name}`];

      dispatch(setAdminOrderErrors(copiedErrors));
    },
    [dispatch, formData?.fields, formError],
  );

  const onChangeTransactionInput = useCallback(
    (field_name, name, field) => (event) => {
      setFormData((prev) => ({
        ...prev,
        transaction: {
          ...prev.transaction,
          name: event.target.value,
          field_name: field_name,
          field: field,
        },
      }));

      const copiedErrors = _.cloneDeep(formError);

      delete copiedErrors[`transaction.${name}`];

      dispatch(setAdminOrderErrors(copiedErrors));
    },
    [dispatch, formError],
  );

  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const startIndex = (currentPage - 1) * perPage;
  const endIndex = startIndex + perPage;
  const itemsToRender = selectedOrder?.histories?.slice(startIndex, endIndex);

  const handlePageChange = (page, pageSize) => {
    setCurrentPage(page);
    setPerPage(pageSize);
  };

  const isTransactions = Object.keys(formData?.transaction)?.length;

  return (
    <div className="application-form">
      <WhiteCard>
        {isLoading ? <PageLoader /> : null}

        <h3>
          {t(`adminApplications:${!isEditMode ? 'create' : 'edit'}`)}
          <div className="application-form__tabs">
            <CustomButton
              title={t('adminApplications:forExchanges')}
              variant={selectedTab === '1' ? 'filled' : 'outlined'}
              onClick={onClickTab('1')}
            />
            <CustomButton
              title={t('adminApplications:fields')}
              variant={selectedTab === '2' ? 'filled' : 'outlined'}
              onClick={onClickTab('2')}
            />
            <CustomButton
              title={t('adminApplications:variables')}
              variant={selectedTab === '3' ? 'filled' : 'outlined'}
              onClick={onClickTab('3')}
            />
            <CustomButton
              title={t('adminApplications:history')}
              variant={selectedTab === '4' ? 'filled' : 'outlined'}
              onClick={onClickTab('4')}
            />
          </div>
        </h3>
        <form onSubmit={onSubmit}>
          <div
            className="application-form__content"
            style={{ gridTemplateColumns: selectedTab !== '4' ? '1fr 1fr 1fr' : '1fr' }}
          >
            {selectedTab === '1' && (
              <>
                <CustomSelect
                  label={t('status')}
                  value={formData?.status}
                  onChange={onChangeSelect('status')}
                  options={updatedStatuses}
                />
                <CustomInput
                  label={t('exchangeCourseGivenCurrency')}
                  value={formData?.rate_given_currency}
                  onChange={onChangeInput('rate_given_currency')}
                  errorMessage={formError?.rate_given_currency}
                />
                <CustomInput
                  label={t('exchangeCourseReceivedCurrency')}
                  value={formData?.rate_received_currency}
                  type="number"
                  onChange={onChangeInput('rate_received_currency')}
                  errorMessage={formError?.rate_received_currency}
                />
                <CustomInput
                  label={t('givingCurrency')}
                  value={formData?.currencyGiven.code}
                  disabled
                />
                <CustomInput
                  label={t('givingSum')}
                  value={formData?.given_sum}
                  type="number"
                  onChange={onChangeInput('given_sum')}
                  errorMessage={formError?.given_sum}
                />
                <CustomInput
                  label={t('paymentId')}
                  value={formData?.payment_transaction_identifier}
                  onChange={onChangeInput('payment_transaction_identifier')}
                  errorMessage={formError?.payment_transaction_identifier}
                />
                <CustomInput
                  label={t('receivedCurrency')}
                  value={formData?.currencyReceived?.code}
                  disabled
                />
                <CustomInput
                  label={t('receivedSum')}
                  value={formData?.received_sum}
                  type="number"
                  onChange={onChangeInput('received_sum')}
                  errorMessage={formError?.received_sum}
                />
                <CustomInput
                  label={t('payId')}
                  value={formData?.pay_transaction_identifier}
                  onChange={onChangeInput('pay_transaction_identifier')}
                  errorMessage={formError?.pay_transaction_identifier}
                />
                <CustomInput
                  label={t('merchantIncomePayments')}
                  value={formData?.incoming_processing_merchant}
                  disabled
                />
                <CustomInput
                  label={t('merchantOutcomePayments')}
                  value={formData?.payment_processing_merchant}
                  disabled
                />
                <CustomInput label={t('timeToPay')} value={formData?.time_to_pay} disabled />
                <CustomInput label={t('createdDate')} value={formData?.created_at} disabled />
                <CustomInput label={t('paymentDate')} value={formData?.payment_date} disabled />
                <CustomInput label={t('dateSuccess')} value={formData?.finish_date} disabled />
                <CustomInput label={t('userLogin')} value={formData?.login} disabled />
                <CustomInput label={t('bonusSum')} value={formData?.bonus} disabled />
                <CustomInput label={t('indentifier')} value={formData?.id} disabled />
                <CustomInput label={t('ipAddress')} value={formData?.ip} disabled />
                <CustomInput label={t('country')} value={formData?.country} disabled />
                <CustomInput label={t('browser')} value={formData?.browser} disabled />
                <CustomInput label={t('token')} value={formData?.token} disabled />
              </>
            )}

            {selectedTab === '2' && (
              <>
                {formData?.fields?.map((item) => (
                  <CustomInput
                    key={item?.field_name}
                    label={item.field_translate}
                    value={item.name}
                    onChange={onChangeFieldsInput(item?.field_name)}
                  />
                ))}
              </>
            )}

            {selectedTab === '3' && isTransactions && (
              <>
                <CustomInput
                  label={formData?.transaction?.field_name}
                  value={formData?.transaction?.name}
                  onChange={onChangeTransactionInput(
                    formData?.transaction?.field_name,
                    formData?.transaction?.name,
                    formData?.transaction?.field,
                  )}
                />
              </>
            )}

            {selectedTab === '4' && (
              <>
                <table className="account-requests__table">
                  <thead>
                    <tr>
                      <th>{t('label:created')}</th>
                      <th>{t('label:status')}</th>
                      <th>{t('label:hash')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {itemsToRender?.map((item) => (
                      <tr key={item.token}>
                        <td>{moment(item.created_at).format('DD.MM.YYYY, HH:mm')}</td>
                        <td>{item?.status ?? '-'}</td>
                        <td>{item?.hash ?? '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <CustomPagination
                  totalItems={selectedOrder?.histories?.length}
                  initialPage={currentPage}
                  initialPerPage={perPage}
                  onPageChange={handlePageChange}
                  withSelect={false}
                />
              </>
            )}
          </div>
          {selectedTab !== '4' ? <CustomButton variant="filled" title={t('title:save')} /> : null}
        </form>
      </WhiteCard>
    </div>
  );
};
